/** @jsxImportSource @emotion/react */
import React, { useEffect } from "react";
import { Amplify } from "aws-amplify";
import { Authenticator, VerifyContact } from "aws-amplify-react";
import { useHistory, useLocation } from "react-router-dom";
import { CognitoSettings } from "../../../config/amplify-configure";
import { HomeBuilderSignIn } from "./HomeBuilderSignIn";
import { HomeBuilderRequireNewPassword } from "./HomeBuilderRequireNewPassword";
import { BootstrapUsecase } from "../../../usecases/Bootstrap/usecase";
import { LayoutForm } from "../../../components";
import { AuthState } from "./const";
import { useCustomLocation } from "../../hooks/useLocation";
import * as Styles from "./styles";
import { LocalStorage } from "../../../modules/LocalStorage";
import { Layout } from "antd";
import {
  AuthUsecase,
  useAuthDispatch,
  useAuthState,
} from "../../../usecases/Auth";
import { SettingUsecase, useSettingDispatch } from "../../../usecases/Setting";

const HomeBuilderLoginComponent = () => {
  const customLocation = useCustomLocation();
  const location = useLocation<{ from: string }>();
  const history = useHistory();
  const authDispatch = useAuthDispatch();
  const [loading, setLoading] = React.useState(true);
  const { userInfo } = useAuthState();
  const [cognitoSettings, setCognitoSettings] =
    React.useState<CognitoSettings>();
  const settingDispatch = useSettingDispatch();

  useEffect(() => {
    async function asyncLoadBootstrap() {
      if (settingDispatch) {
        const bootstrapUsecase = new BootstrapUsecase();
        const bootstrapResponse = await bootstrapUsecase.get({
          tenantSubDomain: customLocation.subDomain,
        });

        LocalStorage.setTenantInfo(bootstrapResponse);
        const settingUsecase = new SettingUsecase(settingDispatch);
        settingUsecase.setTenantInfo(bootstrapResponse);

        setCognitoSettings({
          region: "ap-southeast-2",
          userPoolId: bootstrapResponse.tenantUserPoolId,
          userPoolWebClientId: bootstrapResponse.portalClientId,
        });
      }
    }

    if (!customLocation.subDomain.includes("admin")) {
      asyncLoadBootstrap();
    }
  }, [customLocation.subDomain, settingDispatch]);

  useEffect(() => {
    async function asyncAmplifyConfigure() {
      if (cognitoSettings) {
        Amplify.configure(cognitoSettings);
        try {
          if (!userInfo) {
            const authUsecase = new AuthUsecase(authDispatch);
            await authUsecase.syncUserInfo();
          }
        } finally {
          setLoading(false);
        }
      }
    }
    asyncAmplifyConfigure();
  }, [authDispatch, cognitoSettings, userInfo]);

  const onAuthStateChangeHandle = React.useCallback(
    async (state) => {
      if (state === AuthState.SignedIn) {
        const path = "/";
        const { from } = location.state || {
          from: { pathname: path },
        };
        history.push(from);
      }
    },
    [history, location.state],
  );

  if (loading) {
    return <></>;
  }

  return (
    <LayoutForm>
      <Layout.Header css={Styles.headerStyle}>
        <img
          src={LocalStorage.getTenantInfo()?.tenantLogoImage}
          css={Styles.logoStyle}
        />
      </Layout.Header>
      <Layout.Content css={Styles.contentStyle}>
        <Authenticator onStateChange={onAuthStateChangeHandle} hideDefault>
          <HomeBuilderSignIn />
          <HomeBuilderRequireNewPassword />
          <VerifyContact />
        </Authenticator>
      </Layout.Content>
      <Layout.Footer css={Styles.footerStyle}>
        © {new Date().getFullYear()} Away Digital Home. All Rights Reserved.
      </Layout.Footer>
    </LayoutForm>
  );
};

export const HomeBuilderLogin = React.memo(HomeBuilderLoginComponent);
